import { RIDE_ADD_ROUTE_BASES, RIDE_EDIT_ROUTE_BASES } from "../constants";
import {
  BOOKING_CALENDAR_URL_PATH,
  BookingsRoutes,
  CARD_BOARD_URL_PATH,
  GRID_SINGLE_SELECT_URL_PATH,
  KANBAN_URL_PATH,
  MAP_URL_PATH,
  RideRoutes,
  RIDES_CALENDAR_URL_PATH,
  RIDES_URL,
} from "../enums";
import { entityPathMappings, isEntityPathMapped } from "../mappers";
import { RydeUiUnit } from "../models";

/**
 * Pathname → the ride edit base for the active rides view.
 * Pure port of `useRidesNavigation.getEditBasePath` (which delegates here) so grid cells can
 * resolve edit hrefs without instantiating the navigation hook per row.
 */
export const resolveRideEditBasePath = (pathname: string): RideRoutes => {
  if (pathname.includes(CARD_BOARD_URL_PATH)) {
    return RideRoutes.EDIT_BASE_CARD_BOARD;
  }

  if (pathname.includes(KANBAN_URL_PATH)) {
    return RideRoutes.EDIT_BASE_KANBAN;
  }

  if (pathname.includes(GRID_SINGLE_SELECT_URL_PATH)) {
    return RideRoutes.EDIT_BASE_GRID_SINGLE_SELECT;
  }

  if (pathname.includes(MAP_URL_PATH)) {
    return RideRoutes.EDIT_BASE_MAP;
  }

  if (pathname.includes(`${RIDES_URL}${RIDES_CALENDAR_URL_PATH}`)) {
    return RideRoutes.EDIT_BASE_CALENDAR;
  }

  return RideRoutes.EDIT_BASE_GRID_MULTI_SELECT;
};

export const isGridSingleSelectViewPathname = (pathname: string): boolean =>
  pathname.includes(GRID_SINGLE_SELECT_URL_PATH);

export const isKanbanViewPathname = (pathname: string): boolean =>
  pathname.includes(KANBAN_URL_PATH);

export const isRidesEditRoutePathname = (pathname: string): boolean =>
  RIDE_EDIT_ROUTE_BASES.some(base => pathname.includes(base));

export const isRidesAddRoutePathname = (pathname: string): boolean =>
  RIDE_ADD_ROUTE_BASES.some(base => pathname.includes(base));

export const isRidesEditOrAddRoutePathname = (pathname: string): boolean =>
  isRidesEditRoutePathname(pathname) || isRidesAddRoutePathname(pathname);

/**
 * Unprefixed edit href base (ends with `/edit/`) for the grid id column, mirroring
 * `useCrudNavigate.navigateToEdit`'s branching. `undefined` means no href can be built
 * (unmapped / admin-only entities) and the cell keeps its imperative fallback.
 */
export const getEntityEditHrefBase = (
  entity: RydeUiUnit,
  pathname: string,
): string | undefined => {
  if (entity === "unknown" || !isEntityPathMapped(entity)) {
    return undefined;
  }

  if (entity === "ride") {
    return resolveRideEditBasePath(pathname);
  }

  if (entity === "booking") {
    return pathname.includes(BOOKING_CALENDAR_URL_PATH)
      ? BookingsRoutes.EDIT_BASE_CALENDAR
      : BookingsRoutes.EDIT_BASE;
  }

  const entityPath = entityPathMappings[entity];

  return entityPath ? `/${entityPath}/edit/` : undefined;
};
